//@ts-strict-ignore
import React, { ComponentProps, ReactElement, useMemo, useState } from 'react';
import { useActionProps } from '../../../utils/useActionProps';
import LabelPrintModal from '../../modals/LabelPrintModal';
import ModalContext from '../../Context/ModalContext';
import DocumentModal2 from '../../modals/DocumentModal2';
import { ConformityCertificate, DispatchInstructions } from '../../../graphql/entities';
import { IAutomaticDispatchResult, IDispatchInstruction } from '../../../graphql/schema/p4types';
import BoxLabelInstructions from '../../../graphql/entities/sales/BoxLabelInstructions';

type formReloadData = (processingRequired?: boolean) => void;
type workspaceReloadData = () => void;

export type ILabelModalState = { modalOpen: boolean; data?: Array<IDispatchInstruction> };
export type IBoxLabelState = { modalOpen: boolean; data?: Array<IDispatchInstruction>; multiValue?: boolean };
export type IDeliveryNoteModalState = ILabelModalState & {
  multiValue?: boolean;
  values?: any;
  dismissToast?: () => void;
};

//Override of props on DocumentModal2 to indicate the properties that get passed through to actions by that modal.
//Ideally we would provide these via a 'meta: any' prop or similar, but don't wish to risk breaking other logic at this time.
const DocumentModal2WithDispatchInstructionProp = DocumentModal2 as unknown as (
  props: ComponentProps<typeof DocumentModal2> & {
    dispatchInstruction?: IDispatchInstruction;
    overrideActionProps?: Function;
    blockNavigation?: boolean;
  }
) => ReactElement;

export const useDispatchPrintModals = ({
  multiValue,
  reloadData,
  normalizeResults, //Specifically used for the 'automatic dispatch result' workspace to unwrap the dispatch instructions for printing.
  blockNavigation,
  defaultLabelModalState = { modalOpen: false },
}: {
  multiValue?: boolean;
  reloadData?: formReloadData | workspaceReloadData;
  normalizeResults?: boolean;
  blockNavigation?: boolean; //Prevent print process from navigating the user when the print job initiates.
  defaultLabelModalState?: ILabelModalState; //populate and open the print label modal by default
}) => {
  const [labelModalState, setLabelModalState] = useState<ILabelModalState>(defaultLabelModalState);
  const [deliveryNoteModalState, setDeliveryNoteModalState] = useState<IDeliveryNoteModalState>({ modalOpen: false });
  const [conformityCertificateModalState, setConformityCertificateModalState] = useState<IDeliveryNoteModalState>({
    modalOpen: false,
  });
  const [boxLabelModalState, setBoxLabelModalState] = useState<IBoxLabelState>({ modalOpen: false });

  const dispatchPrintModalOpen = useMemo(() => {
    return (
      deliveryNoteModalState?.modalOpen || conformityCertificateModalState?.modalOpen || boxLabelModalState?.modalOpen
    );
  }, [deliveryNoteModalState?.modalOpen, conformityCertificateModalState?.modalOpen, boxLabelModalState?.modalOpen]);

  const actionProps = useActionProps('dispatchInstructions');
  if (normalizeResults) {
    const uniqueDispatchInstructions = {};
    (actionProps.dispatchInstructions as Array<IAutomaticDispatchResult>).forEach(
      (value) => (uniqueDispatchInstructions[value.dispatchInstruction?.id] = value.dispatchInstruction)
    );
    actionProps.dispatchInstructions = Object.values(uniqueDispatchInstructions);
  }

  let deliveryNoteActionProps;
  if (deliveryNoteModalState.data) {
    deliveryNoteActionProps = () => {
      return {
        filters: [],
        [`dispatchInstructions`]: deliveryNoteModalState.data,
        include: true,
      };
    };
  } else if (actionProps) {
    deliveryNoteActionProps = () => actionProps;
  }

  let boxLabelActionProps;
  if (boxLabelModalState.data) {
    boxLabelActionProps = () => {
      return {
        filters: [],
        dispatchInstructions: boxLabelModalState.data,
        include: true,
      };
    };
  } else if (actionProps) {
    boxLabelActionProps = () => actionProps;
  }

  let conformityCertificateActionProps;
  if (conformityCertificateModalState.data) {
    conformityCertificateActionProps = () => {
      return {
        filters: [],
        [`dispatchInstructions`]: conformityCertificateModalState.data,
        include: true,
      };
    };
  } else if (actionProps) {
    conformityCertificateActionProps = () => actionProps;
  }

  const printModals = (
    <>
      <LabelPrintModal
        isOpen={labelModalState.modalOpen}
        data={labelModalState.data}
        onClose={() => {
          setLabelModalState({ ...labelModalState, modalOpen: false });
          if (!deliveryNoteModalState.modalOpen || conformityCertificateModalState.modalOpen) {
            reloadData?.(true);
          }
        }}
      />

      <ModalContext.Provider
        value={{
          onClose: () => {
            setBoxLabelModalState({ ...boxLabelModalState, modalOpen: false });
            reloadData?.(true);
          },
        }}
      >
        <DocumentModal2WithDispatchInstructionProp
          modalOpen={!labelModalState.modalOpen && boxLabelModalState.modalOpen}
          multiValue={
            (boxLabelModalState.multiValue || multiValue) &&
            (!boxLabelModalState.data || boxLabelModalState.data.length > 1)
          }
          entity={BoxLabelInstructions}
          useCustomerDefaults={true}
          title="Print Box Labels"
          values={boxLabelModalState.data}
          modalWrapper={true}
          dispatchInstruction={boxLabelModalState.data?.[0]}
          overrideActionProps={boxLabelActionProps}
          allowResetToStart={true}
          blockNavigation={blockNavigation}
        />
      </ModalContext.Provider>

      <ModalContext.Provider
        value={{
          onClose: () => {
            setDeliveryNoteModalState({ ...deliveryNoteModalState, modalOpen: false });
            deliveryNoteModalState?.dismissToast?.();
            reloadData?.(true);
          },
        }}
      >
        <DocumentModal2WithDispatchInstructionProp
          modalOpen={!labelModalState.modalOpen && !boxLabelModalState.modalOpen && deliveryNoteModalState.modalOpen}
          multiValue={
            (deliveryNoteModalState.multiValue || multiValue) &&
            (!deliveryNoteModalState.data || deliveryNoteModalState.data.length > 1)
          }
          entity={DispatchInstructions}
          useCustomerDefaults={true}
          title="Print Delivery Note"
          values={deliveryNoteModalState.values}
          modalWrapper={true}
          dispatchInstruction={deliveryNoteModalState.data?.[0]}
          overrideActionProps={deliveryNoteActionProps}
          allowResetToStart={true}
          blockNavigation={blockNavigation}
        />
      </ModalContext.Provider>
      <ModalContext.Provider
        value={{
          onClose: () => {
            setConformityCertificateModalState({ ...conformityCertificateModalState, modalOpen: false });
            reloadData?.(true);
          },
        }}
      >
        <DocumentModal2WithDispatchInstructionProp
          modalOpen={
            !labelModalState.modalOpen &&
            !deliveryNoteModalState.modalOpen &&
            !boxLabelModalState.modalOpen &&
            conformityCertificateModalState.modalOpen
          }
          multiValue={
            (conformityCertificateModalState.multiValue || multiValue) &&
            (!deliveryNoteModalState.data || deliveryNoteModalState.data.length > 1)
          }
          entity={ConformityCertificate}
          useCustomerDefaults={true}
          title="Print Conformity Certificate"
          values={conformityCertificateModalState.values}
          modalWrapper={true}
          dispatchInstruction={conformityCertificateModalState.data?.[0]}
          overrideActionProps={conformityCertificateActionProps}
          allowResetToStart={true}
          blockNavigation={blockNavigation}
        />
      </ModalContext.Provider>
    </>
  );

  const result: [
    {
      setLabelModalState: React.Dispatch<React.SetStateAction<ILabelModalState>>;
      setDeliveryNoteModalState: React.Dispatch<React.SetStateAction<IDeliveryNoteModalState>>;
      setConformityCertificateModalState: React.Dispatch<React.SetStateAction<IDeliveryNoteModalState>>;
      setBoxLabelModalState: React.Dispatch<React.SetStateAction<IBoxLabelState>>;
      dispatchPrintModalOpen: boolean;
    },
    ReactElement
  ] = [
    {
      setLabelModalState,
      setDeliveryNoteModalState,
      setConformityCertificateModalState,
      setBoxLabelModalState,
      dispatchPrintModalOpen,
    },
    printModals,
  ];
  return result;
};
